import { Search } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useContext, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { FixedSizeList as List } from "react-window";
import styled from "styled-components";
import { Wrapper } from "common/styled.modals";
import { assignTask } from "api/tasks";
import { DialogTitle } from "components/Dialog";
import Loader from "components/Loader/Loader";
import { capitalize } from "common/utils";
import { CustomText, getSingleIconUser, NameIcon } from "common/Reusables";
import { getMethod } from "hooks/getHook";
import { AuthContext } from "api/AuthContext";
import { socket } from "socket";
import { enqueueSnackbar } from "notistack";
import { postReq } from "hooks/postHook";
import { backgroundColors } from "../utils";

const EmployeeCard = ({ isSelected, handleSelect, details, style, index }) => {
  const [avatarBackgroundColor] = useState(
    details?.photo
      ? null
      : backgroundColors[Math.floor(Math.random() * backgroundColors.length)]
  );
  return (
    <EmployeeSC style={style}>
      <Checkbox
        className="check"
        checked={isSelected}
        onChange={handleSelect}
        size="small"
        style={{ color: "#2898A4", paddingLeft: 0 }}
      />
      {details?.photo ? (
        <Avatar src={details?.photo} />
      ) : (
        <Avatar
          style={{
            backgroundColor: avatarBackgroundColor,
            borderRadius: "10px",
          }}
          variant="rounded"
        >
          <CustomText fontSize="12px" fontWeight="600" color="#000">
            {getSingleIconUser(details?.first_name)}
          </CustomText>
        </Avatar>
      )}
      <div className="about">
        <Typography
          sx={{
            fontWeight: 700,
            mb: 0.7,
          }}
          variant="body2"
        >
          {capitalize(details?.first_name || "") +
            " " +
            capitalize(details?.last_name || "")}
        </Typography>
        <Typography variant="subtitle2" textTransform={"capitalize"}>
          {details?.job?.title}
        </Typography>
      </div>
      <Typography
        variant="caption"
        textTransform={"capitalize"}
        className="job"
      >
        {details?.department?.name}
      </Typography>
    </EmployeeSC>
  );
};
const DepartmentCard = ({
  isSelected,
  handleSelect,
  details,
  style,
  index,
}) => {
  return (
    <EmployeeSC style={style}>
      <Checkbox
        className="check"
        checked={isSelected}
        onChange={handleSelect}
        size="small"
        style={{ color: "#2898A4", paddingLeft: 0 }}
      />
      <NameIcon first={details?.name} width={20} height={20} />
      <div className="about">
        <Typography
          sx={{
            fontWeight: 700,
            mb: 0.7,
          }}
          variant="body2"
        >
          {capitalize(details?.name || "")}
        </Typography>
      </div>
    </EmployeeSC>
  );
};



const EmployeeSC = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 4fr 2fr;
  padding-block: 0.7rem;
  border-bottom: 1px solid #e1e1e1;
  align-items: center;
  font-family: "Black Sans";
  .about {
    margin-right: 2rem;
  }
`;

export default function ShareTaskModal({
  selectedData,
  open,
  toggleOpenModal: toggle,
}) {
  const [selected, setSelected] = useState([]);

  const [params, setParams] = useState({});
  const { user } = useContext(AuthContext);

  const domain = localStorage.getItem("business_id");

  const [toBeAssigned, setToBeAssigned] = useState([]);

  const searchRef = useRef(null);

  const { data, isLoading, refetch } = useQuery(
    [
      "employees",
      {
        route: `/employees/${user?.id}/employee_filter_myedge`,
        params: params,
      },
    ],
    getMethod
  );
  const {
    data: groupsData,
    isLoading: isLoadingGroup,
    refetch: refetchGroups,
  } = useQuery(
    [
      "groups",
      {
        route: `/groups/get_employee_groups/${user?.id}`,
        params: params,
      },
    ],
    getMethod
  );
  const mergeAndSortData = (employees, groups) => {
    const standardizedEmployees = employees.map((emp) => ({
      ...emp,
      type: "employee",
      name: `${capitalize(emp.first_name || "")} ${capitalize(
        emp.last_name || ""
      )}`,
    }));

    const standardizedGroups = groups.map((grp) => ({
      ...grp,
      type: "group",
      name: capitalize(grp.name || ""),
    }));

    return [...standardizedEmployees, ...standardizedGroups].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  };

  const mergedData = mergeAndSortData(data || [], groupsData || []);
 
  const filteredData = mergedData.filter((item) =>
    item.name.toLowerCase().includes(params.search?.toLowerCase() || "")
  );
  const createRoomMutation = useMutation(postReq, {
    onError: (error) => {
      setSelected(null);
    },
    onSuccess: ({ data }) => {
      refetch();
    },
  });

  const handleSelect = (isSelected, id, item) => {
    setSelected((prevSelected) => {
      if (isSelected) {
        return [...prevSelected, id];
      } else {
        return prevSelected.filter((selectedId) => selectedId !== id);
      }
    });

    setToBeAssigned((prevToBeAssigned) => {
      if (isSelected) {
        return [...prevToBeAssigned, item];
      } else {
        return prevToBeAssigned.filter(
          (assignedItem) => assignedItem.id !== id
        );
      }
    });
  };

  const assignMutation = useMutation(assignTask);

  const handleAssign = async () => {
    const { id, title, description } = selectedData;
    const url = `tasks?taskId=${id}`;
    const messagePayload = JSON.stringify({ id, title, description, url });

    for (const item of toBeAssigned) {
     
      if (item.type === "employee") {
        if (item.chat_room) {
          socket.emit("chat", {
            chat_room: item.chat_room,
            message: messagePayload,
            sender: user?.id,
            receiver: item.id,
            message_type: "shared_task",
            business_id: domain,
          });
        } else {
          const { data } = await createRoomMutation.mutateAsync({
            route: "/chat_rooms/",
            payload: {
              employee_one: user.id,
              employee_two: item.id,
              pin: false,
              mute_chat: false,
            },
          });

          socket.emit("chat", {
            chat_room: data?.id,
            message: messagePayload,
            sender: user?.id,
            receiver: item.id,
            message_type: "shared_task",
            business_id: domain,
          });
        }
      } else if (item.type === "group") {
        socket.emit("group-chat", {
          group: item.id,
          message: messagePayload,
          sender: user?.id,
          message_type: "shared_task",
          business_id: domain,
        });
      }
    }

    enqueueSnackbar(`Task shared successfully`, { variant: "success" });
    toggle();
  };

  return (
    <Modal
      fullWidth={false}
      maxWidth={"xs"}
      onClose={toggle}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <Wrapper
        width="550px"
        widthPercent="70%"
        padding="20px"
        // style={{ position: 'relative' }}
      >
        <ShareTaskWrapper>
          <DialogTitle
            iconStyle={{
              top: 10,
            }}
            id="customized-dialog-title"
            sx={{
              py: 1,
            }}
            onClose={toggle}
          >
            <Typography variant="h6" fontWeight={700}>
              Share Task
            </Typography>
          </DialogTitle>
          <Divider
            sx={{
              marginInline: "1rem",
            }}
          />
          <div className="content">
            <TextField
              className="search_input"
              size="small"
              id=""
              label=""
              placeholder="Search for employee or group"
              ref={searchRef}
              value={params?.search}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#ffffff",
                  "&.Mui-focused fieldset": {
                    borderColor: "#e1e1e1",
                  },
                },}}
              onChange={(e) => {
                setParams((prev) => ({ ...prev, search: e.target.value }));
              }}
              InputProps={{
                startAdornment: <Search position="start" />,
              }}
            />
          
            <Box
              my="1rem"
              className="employees_container"
              sx={{ maxHeight: "320px", overflowY: "auto" }}
            >
              <div className="employees">
                {isLoading || isLoadingGroup ? (
                  <Loader />
                ) : filteredData.length < 1 ? (
                  "No Data"
                ) : (
                  // <List
                  //   height={320}
                  //   width={"100%"}
                  //   itemCount={filteredData.length}
                  //   itemData={filteredData}
                  //   itemSize={80}
                  // >
                  //   {({ index, style }) => {
                  //     const item = filteredData[index];
                  //     return item.type === "employee" ? (
                  //       <EmployeeCard
                  //         style={style}
                  //         details={item}
                  //         isSelected={selected.some((el) => el === item?.id)}
                  //         handleSelect={(e) => {
                  //           handleSelect(!!e.target.checked, item?.id, item);
                  //         }}
                  //         key={item?.id + item?.first_name + index}
                  //       />
                  //     ) : (
                  //       <DepartmentCard
                  //         style={style}
                  //         details={item}
                  //         isSelected={selected.some((el) => el === item?.id)}
                  //         handleSelect={(e) => {
                  //           handleSelect(!!e.target.checked, item?.id, item);
                  //         }}
                  //         key={item?.id + item?.name + index}
                  //       />
                  //     );
                  //   }}
                  // </List>
                  <List
                    height={320}
                    width={"100%"}
                    itemCount={filteredData.length}
                    itemData={filteredData}
                    itemSize={80}
                  >
                    {({ index, style }) => {
                      const item = filteredData[index];
                      const isSelected = selected.includes(item.id);

                      return item.type === "employee" ? (
                        <EmployeeCard
                          style={style}
                          details={item}
                          isSelected={isSelected}
                          handleSelect={(e) =>
                            handleSelect(e.target.checked, item.id, item)
                          }
                          key={item.id}
                        />
                      ) : (
                        <DepartmentCard
                          style={style}
                          details={item}
                          isSelected={isSelected}
                          handleSelect={(e) =>
                            handleSelect(e.target.checked, item.id, item)
                          }
                          key={item.id}
                        />
                      );
                    }}
                  </List>
                )}
              </div>
            </Box>
          </div>
          {filteredData?.length > 0 && (
            <Button
              className="btn_assign"
              sx={{ width: "100%" }}
              textAlign="center"
              disabled={selected.length < 1}
              onClick={handleAssign}
            >
              {assignMutation.isLoading ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Share"
              )}
            </Button>
          )}
        </ShareTaskWrapper>
      </Wrapper>
    </Modal>
  );
}


export const ShareTaskWrapper = styled.div`
  /* border: 2px solid red; */
  .content {
    display: flex;
    flex-direction: column;
    /* gap: 0.5rem; */
    padding: 0 1rem;
    /* border: 2px solid red; */
  }
  .employees_container {
    list-style: none;
  }
  .employees_container::-webkit-scrollbar {
    width: 1em;
  }

  .employees_container::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  .employees_container::-webkit-scrollbar-thumb {
    background-color: darkgrey;
    outline: 1px solid slategrey;
  }
  .btn_assign {
    background: #2898a4;
    color: #ffffff;
    font-weight: 600;
    &:hover {
      background-color: #2898a4;
    }
  }
`;
